import { useEffect, useState, type FormEvent } from 'react';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import type { BuiltInVariable, MarketingCustomVariable } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * The Variables tab. Built-in variables are the server's catalogue
 * (`BUILT_IN_VARIABLES`) and are read-only here; custom variables are rows an
 * administrator keeps, used in a template as `{{ custom.<key> }}`.
 *
 * Writes are gated on `marketing-templates.update` — the routes ask that action
 * for create, edit and delete alike.
 */

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const KNOWN_KEYS = ['key', 'label', 'value', 'description'] as const;

function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return caught instanceof Error ? caught.message : fallback;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`Copy ${label}`}
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function VariableToken({ variableKey }: { variableKey: string }) {
  const token = `{{ ${variableKey} }}`;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <code className="rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-[11px] text-fg-secondary [overflow-wrap:anywhere]">
        {token}
      </code>
      <CopyButton text={token} label={token} />
    </div>
  );
}

interface Draft {
  key: string;
  label: string;
  value: string;
  description: string;
}

function draftOf(variable: MarketingCustomVariable | null): Draft {
  return {
    key: variable?.key ?? '',
    label: variable?.label ?? '',
    value: variable?.value ?? '',
    description: variable?.description ?? '',
  };
}

function VariableFormModal({
  open,
  variable,
  onClose,
  onSaved,
}: {
  open: boolean;
  variable: MarketingCustomVariable | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(draftOf(null));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(draftOf(variable));
    setFieldErrors({});
    setFormError(null);
  }, [open, variable]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    const local: Record<string, string> = {};
    const key = draft.key.trim();
    if (!KEY_PATTERN.test(key)) {
      local.key = 'Use lowercase letters, digits and underscores, starting with a letter (64 at most).';
    }
    if (!draft.label.trim()) local.label = 'Enter a label.';
    setFieldErrors(local);
    setFormError(null);
    if (Object.keys(local).length > 0) return;

    const description = draft.description.trim() ? draft.description.trim() : null;
    setSaving(true);
    try {
      if (variable) {
        const original = draftOf(variable);
        const patch: Partial<{ key: string; label: string; value: string; description: string | null }> = {};
        if (key !== original.key) patch.key = key;
        if (draft.label.trim() !== original.label) patch.label = draft.label.trim();
        if (draft.value !== original.value) patch.value = draft.value;
        if (description !== variable.description) patch.description = description;
        if (Object.keys(patch).length > 0) await api.updateMarketingVariable(variable.id, patch);
      } else {
        await api.createMarketingVariable({ key, label: draft.label.trim(), value: draft.value, description });
      }
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const routed = caught.routeFieldErrors(KNOWN_KEYS);
        const fields = { ...routed.fields };
        if (caught.status === 409 && !fields.key) fields.key = caught.message;
        setFieldErrors(fields);
        setFormError(
          routed.unroutable.length > 0
            ? routed.unroutable.join(' ')
            : Object.keys(fields).length > 0
              ? null
              : caught.message,
        );
      } else {
        setFormError('Could not reach the server. Nothing was saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={variable ? `Edit ${variable.label}` : 'New variable'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="marketing-variable-form" loading={saving}>
            {variable ? 'Save changes' : 'Create variable'}
          </Button>
        </>
      }
    >
      <form id="marketing-variable-form" className="grid gap-4" onSubmit={(e) => void submit(e)} noValidate>
        {formError && (
          <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
            {formError}
          </Notice>
        )}
        <Field label="Key" required error={fieldErrors.key}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={draft.key}
              maxLength={64}
              spellCheck={false}
              autoCapitalize="off"
              className="font-mono"
              invalid={Boolean(fieldErrors.key)}
              onChange={(e) => set('key', e.target.value)}
            />
          )}
        </Field>
        <Field label="Label" required error={fieldErrors.label}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={draft.label}
              maxLength={120}
              invalid={Boolean(fieldErrors.label)}
              onChange={(e) => set('label', e.target.value)}
            />
          )}
        </Field>
        <Field label="Value" error={fieldErrors.value}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={draft.value}
              rows={3}
              invalid={Boolean(fieldErrors.value)}
              onChange={(e) => set('value', e.target.value)}
            />
          )}
        </Field>
        <Field label="Description" error={fieldErrors.description}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={draft.description}
              maxLength={500}
              invalid={Boolean(fieldErrors.description)}
              onChange={(e) => set('description', e.target.value)}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}

export function VariablesManager() {
  const { ability } = useAuth();
  const canWrite = ability.canUpdate('marketing-templates');

  const [builtIn, setBuiltIn] = useState<BuiltInVariable[] | null>(null);
  const [custom, setCustom] = useState<MarketingCustomVariable[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [editing, setEditing] = useState<{ variable: MarketingCustomVariable | null } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MarketingCustomVariable | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api
      .listMarketingVariables(controller.signal)
      .then((response) => {
        setBuiltIn([...response.data.builtIn]);
        setCustom([...response.data.custom]);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorText(caught, 'Variables could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteMarketingVariable(pendingDelete.id);
      setPendingDelete(null);
      setReloadKey((k) => k + 1);
    } catch (caught) {
      setDeleteError(errorText(caught, 'Could not reach the server. Nothing was deleted.'));
    } finally {
      setDeleting(false);
    }
  }

  const initialLoad = loading && builtIn === null;

  return (
    <div className="grid min-w-0 gap-4">
      {error && (
        <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load variables.</>}>
          <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
        </Notice>
      )}

      <Panel className="min-w-0" aria-labelledby="marketing-custom-variables-heading">
        <PanelHeader
          id="marketing-custom-variables-heading"
          title="Custom variables"
          description={custom ? `${custom.length} ${custom.length === 1 ? 'variable' : 'variables'}` : undefined}
          actions={
            canWrite ? (
              <Button variant="primary" size="sm" leadingIcon={<PlusIcon />} onClick={() => setEditing({ variable: null })}>
                New variable
              </Button>
            ) : undefined
          }
        />
        {initialLoad ? (
          <div className="grid gap-2 p-4">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : !custom || custom.length === 0 ? (
          <EmptyState
            title={error ? 'Nothing to show' : 'No custom variables yet'}
            description={
              error ? 'Couldn’t load the list — see the message above.' : 'A custom variable is one value kept here and reused in any template.'
            }
          />
        ) : (
          <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
            <Table className="min-w-[760px]">
              <THead>
                <TR>
                  <TH className="w-64">Variable</TH>
                  <TH className="w-44">Label</TH>
                  <TH>Value</TH>
                  <TH className="w-28" numeric>
                    Actions
                  </TH>
                </TR>
              </THead>
              <TBody>
                {custom.map((variable) => (
                  <TR key={variable.id}>
                    <TD>
                      <VariableToken variableKey={`custom.${variable.key}`} />
                    </TD>
                    <TD>
                      <p className="m-0 text-sm text-fg [overflow-wrap:anywhere]">{variable.label}</p>
                      {variable.description && (
                        <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">{variable.description}</p>
                      )}
                    </TD>
                    <TD className="text-fg-secondary">
                      {variable.value ? (
                        <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{variable.value}</span>
                      ) : (
                        <span className="text-fg-subtle">Empty</span>
                      )}
                    </TD>
                    <TD numeric>
                      {canWrite && (
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            leadingIcon={<PencilIcon />}
                            aria-label={`Edit ${variable.label}`}
                            onClick={() => setEditing({ variable })}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            iconOnly
                            leadingIcon={<TrashIcon />}
                            aria-label={`Delete ${variable.label}`}
                            onClick={() => {
                              setDeleteError(null);
                              setPendingDelete(variable);
                            }}
                          />
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        )}
      </Panel>

      <Panel className="min-w-0" aria-labelledby="marketing-builtin-variables-heading">
        <PanelHeader
          id="marketing-builtin-variables-heading"
          title="Built-in variables"
          description={builtIn ? `${builtIn.length} ${builtIn.length === 1 ? 'variable' : 'variables'}` : undefined}
        />
        {initialLoad ? (
          <div className="grid gap-2 p-4">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        ) : !builtIn || builtIn.length === 0 ? (
          <EmptyState title="Nothing to show" description={error ? 'Couldn’t load the list — see the message above.' : undefined} />
        ) : (
          <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
            <Table className="min-w-[760px]">
              <THead>
                <TR>
                  <TH className="w-72">Variable</TH>
                  <TH className="w-56">Label</TH>
                  <TH className="w-32">Group</TH>
                  <TH>Example</TH>
                </TR>
              </THead>
              <TBody>
                {builtIn.map((variable) => (
                  <TR key={variable.key}>
                    <TD>
                      <VariableToken variableKey={variable.key} />
                    </TD>
                    <TD className="text-fg">{variable.label}</TD>
                    <TD className="text-fg-secondary">{variable.group}</TD>
                    <TD className="text-fg-secondary [overflow-wrap:anywhere]">{variable.example}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        )}
      </Panel>

      <VariableFormModal
        open={editing !== null}
        variable={editing?.variable ?? null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          setReloadKey((k) => k + 1);
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        tone="danger"
        title={pendingDelete ? `Delete ${pendingDelete.label}?` : 'Delete variable?'}
        description={
          pendingDelete
            ? `It can't be recovered. Templates that use {{ custom.${pendingDelete.key} }} will render it empty from now on.`
            : ''
        }
        confirmLabel="Delete variable"
        loading={deleting}
        error={deleteError && <span className="[overflow-wrap:anywhere]">{deleteError}</span>}
      />
    </div>
  );
}
